<div class="absolute inset-0 flex flex-col flex-auto min-w-0">
  <!-- `overflow-y-auto` here, on the page box itself: the scroll used to live
       on the section BELOW the greeting, which left the date + greeting strip
       pinned at the top while the cards moved under it — a sticky header
       nobody asked for. With the whole page scrolling, the greeting leaves
       with the content and the scrollbar sits at the page's edge, outside
       the padding, instead of inside it beside the cards. -->
  <main class="page-container w-full pc-page-enter overflow-y-auto">
    <!-- Hero: today's date as an engraved eyebrow, then the greeting -->
    <header class="shrink-0 flex items-end justify-between gap-3 pb-1">
      <div class="min-w-0 flex-1">
        <p
          class="pc-hero-enter text-pc-eyebrow font-bold uppercase tracking-widest leading-tight text-slate-500 dark:text-neutral-500 mb-1"
        >
          {{ todayLabel() }}
        </p>
        <h1
          class="pc-hero-enter pc-hero-enter-delayed text-pc-title md:text-2xl font-bold text-slate-700 dark:text-white tracking-tight truncate"
        >
          {{ isNationalGreeting ? greeting : (greeting | translate) }},
          <!-- The name and the mark beside it ride in one inline-flex row,
               centred against each other. Aligning the chip on the text
               BASELINE never settled: an empty box has no baseline of its own,
               so the browser synthesises one from its bottom edge and any
               wrapper's line box shifts it again. Centring is the thing being
               asked for anyway — the flag should sit level with the word. -->
          <span class="inline-flex items-center gap-1.5 align-baseline">
            {{ userFirstName }}
            @if (greetingFlag) {
              <!-- Its own entrance, not `pc-hero-emoji`: that one keeps waving
                   on an 8s cycle, which was written for 👋. A national flag
                   rocking back and forth reads as a gimmick, and caught
                   mid-rotation it simply looks crooked. -->
              <primecontrol-flag-chip
                class="pc-hero-flag"
                [country]="greetingFlag"
              ></primecontrol-flag-chip>
            } @else {
              <span class="pc-hero-emoji inline-block">{{ greetingEmoji }}</span>
            }
          </span>
        </h1>
      </div>

      <div class="flex items-center gap-2 shrink-0">
        <primecontrol-button
          *appHasPermission="['Instances', [permissions.Instances.SendInvitation]]"
          class="pc-toolbar-cta"
          [label]="'INVITE'"
          [icon]="'feather:user-plus'"
          (event)="openSendInvitation()"
        ></primecontrol-button>
      </div>
    </header>

    <!-- Content column. Not the scroll area any more — <main> scrolls, so the
         greeting above travels with the cards. `shrink-0` because <main> is a
         fixed-height flex column: without it, the column would be squashed to
         fit and nothing would overflow, so nothing would scroll. No bottom
         padding of its own: <main>'s pb-2 is the sidebar's inset, so the last
         card ends level with the sidebar. -->
    <section class="flex flex-col gap-y-6 mt-3 shrink-0">
      <!-- General info -->
      <!-- shrink-0: pc-glass-card sets overflow:hidden, which lets the scroll
         column's flex layout squash the card to zero height without it. -->
      <article class="pc-glass-card pc-card-static pc-credential-card pc-home-enter shrink-0">
        <div class="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <!-- Instance logo (branding preference); initials chip as fallback -->
          @if (workspaceLogo()) {
            <img
              [src]="workspaceLogo()"
              alt=""
              draggable="false"
              (contextmenu)="$event.preventDefault()"
              class="h-14 max-w-32 object-contain object-left shrink-0"
            />
          } @else {
            <div
              class="flex items-center justify-center w-14 h-14 rounded-2xl shrink-0 text-white text-pc-title-sm font-bold uppercase shadow-sm ring-2 ring-white/15"
              style="background-color: var(--main-button-bg)"
            >
              {{ getInitials() }}
            </div>
          }

          <!-- Identity -->
          <div class="flex flex-col min-w-0 flex-1 gap-1">
            <div
              class="text-pc-eyebrow font-bold uppercase tracking-widest leading-tight text-slate-500 dark:text-neutral-500"
            >
              {{ 'WORKSPACE' | translate }}
            </div>
            <h2
              class="text-pc-title font-semibold tracking-tight leading-tight text-[color:var(--text-title)] truncate"
            >
              {{ instance()?.name }}
            </h2>
            @if (instance()?.description) {
              <p class="text-pc-body leading-snug text-[color:var(--text-subtitle)] line-clamp-2">
                {{ instance().description }}
              </p>
            }

            <!-- Meta chips -->
            <div class="flex flex-wrap items-center gap-2 mt-1">
              @if (instance()?.timeZoneReference) {
                <span
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-pc-label font-medium text-[color:var(--text-subtitle)] bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200/70 dark:border-neutral-700/50"
                >
                  <primecontrol-svg-icon
                    class="icon-size-3.5 opacity-70"
                    [svgIcon]="'feather:clock'"
                  ></primecontrol-svg-icon>
                  {{ instance().timeZoneReference }}
                </span>
              }
              @if (instance()?.defaultLanguage) {
                <span
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-pc-label font-medium text-[color:var(--text-subtitle)] bg-neutral-50 dark:bg-neutral-800/60 border border-neutral-200/70 dark:border-neutral-700/50"
                >
                  <primecontrol-svg-icon
                    class="icon-size-3.5 opacity-70"
                    [svgIcon]="'mat_outline:language'"
                  ></primecontrol-svg-icon>
                  {{ instance().defaultLanguage }}
                </span>
              }
            </div>
          </div>
        </div>
      </article>

      <!-- Summary metrics -->
      <div class="flex flex-col gap-y-3">
        <div class="flex flex-wrap items-center gap-x-3 gap-y-2 px-1">
          <h3
            class="text-pc-eyebrow font-bold uppercase tracking-widest leading-tight text-slate-500 dark:text-neutral-500 shrink-0"
          >
            {{ 'SUMMARY' | translate }}
          </h3>
          <div class="pc-engraved-rule flex-1 min-w-8" aria-hidden="true"></div>

          <!-- Data freshness + manual refresh -->
          @if (updatedAgo(); as ago) {
            <span
              class="text-pc-label font-medium tabular-nums text-[color:var(--text-subtitle)] shrink-0"
            >
              {{ ago.key | translate: ago.params }}
            </span>
          }
          <button
            type="button"
            (click)="refresh()"
            [title]="'REFRESH' | translate"
            [attr.aria-label]="'REFRESH' | translate"
            class="pc-touch-target inline-flex items-center justify-center w-6 h-6 rounded-md text-[color:var(--text-subtitle)] hover:text-[color:var(--text-title)] hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors cursor-pointer shrink-0"
          >
            <primecontrol-svg-icon
              class="icon-size-3.5 pc-icon-spin"
              [ngClass]="{ 'animate-spin': refreshing() }"
              [svgIcon]="'mat_solid:refresh'"
            ></primecontrol-svg-icon>
          </button>
        </div>
        <div
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pc-metric-stagger"
        >
          @if (analyticsError() && !instance()?.analytics) {
            <div class="col-span-full flex items-center justify-center py-6">
              <primecontrol-empty-data-particules
                [message]="'DASHBOARD_LOAD_ERROR'"
                [icon]="'heroicons_outline:exclamation-circle'"
                [size]="'compact'"
              ></primecontrol-empty-data-particules>
            </div>
          } @else if (!instance()?.analytics) {
            <!-- Glass skeletons (same recipe as the instance selector) while
               the analytics response is in flight -->
            @for (i of skeletonItems; track i) {
              <article class="pc-glass-card pc-skeleton-card w-full h-36" aria-hidden="true">
                <div class="pc-skeleton-aurora"></div>
                <div class="pc-skeleton-ring"></div>
                <div class="relative z-10 w-full h-full flex items-center justify-center">
                  <div class="pc-skeleton-tile">
                    <div class="pc-skeleton-tile-core"></div>
                  </div>
                </div>
              </article>
            }
          } @else {
            @for (metric of analyticsWithColors(); track $index) {
              <!-- A metric without a route is a plain tile: binding [undefined]
                   linked it to /undefined. -->
              <primecontrol-metric-card
                [routerLink]="metric.route ? [metric.route] : null"
                [metric]="metric"
                [attr.role]="metric.route ? 'link' : null"
                (keydown.enter)="metric.route && onMetricEnter($event)"
                (keydown.space)="metric.route && onMetricEnter($event)"
              ></primecontrol-metric-card>
            }
          }
        </div>
      </div>

      <!-- Commercial flow: closed money above, funnel and losses beneath it -->
      <div class="pc-engraved-rule mx-1" aria-hidden="true"></div>
      <div class="flex flex-col gap-4 pc-charts-enter">
        <primecontrol-closed-months-card
          [summary]="salesSummary()"
          [previous]="salesSummaryPrevious()"
          [loading]="salesSummaryLoading()"
          [error]="salesSummaryError()"
          (periodChange)="onSalesSummaryPeriodChange($event)"
        ></primecontrol-closed-months-card>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <primecontrol-funnel-card
            [summary]="salesSummary()"
            [loading]="salesSummaryLoading()"
            [error]="salesSummaryError()"
          ></primecontrol-funnel-card>
          <primecontrol-lost-card
            [summary]="salesSummary()"
            [loading]="salesSummaryLoading()"
            [error]="salesSummaryError()"
          ></primecontrol-lost-card>
        </div>
      </div>
    </section>
  </main>
</div>
